import { localeLabel } from "@/lib/locale";
import type { Locale } from "@/lib/locale";
// Single source of truth for the client-onboarding & enrichment vocabulary
// (the role-fork + the "tell us about your search" enrichment form). Mirrors
// the requests.ts pattern: stable enum values + locale-aware display labels.
// NOT a "use server" file — it exports constants, so the server actions and the
// Zod schema can both import from here. See client-onboarding-spec.md.

// ── Onboarding state ──────────────────────────────────────────────────────────

/** What the user picked in the role-fork modal. null (no row / unset) = not chosen. */
export const ONBOARDING_CHOICES = ["pro", "client"] as const;
export type OnboardingChoice = (typeof ONBOARDING_CHOICES)[number];

/** Outcome of the client enrichment step. null = neither yet. */
export const ENRICHMENT_STATUSES = ["saved", "skipped"] as const;
export type EnrichmentStatus = (typeof ENRICHMENT_STATUSES)[number];

/** Auto-show a prompt at most this many times, then it demotes to a passive,
 *  dismissible card (never auto-opens again). Nothing blocks the dashboard. */
export const PROMPT_CAP = 2;

// ── Enrichment: intention ─────────────────────────────────────────────────────

/** "What brings you to Vietnam?" — drives an optional category nudge + future
 *  ranking prior. 'other' reveals a short free-text field (intention_other). */
export const INTENTION_VALUES = ["work", "study", "tourism", "business", "other"] as const;
export type Intention = (typeof INTENTION_VALUES)[number];

/** Max length for the free-text shown when intention === 'other'. Mirrors the
 *  DB CHECK on client_preferences.intention_other. */
export const INTENTION_OTHER_MAX = 100;

const INTENTION_LABELS: Record<Intention, string> = {
  work: "Work",
  study: "Study",
  tourism: "Tourism",
  business: "Business",
  other: "Other",
};

const INTENTION_LABELS_FR: Record<Intention, string> = {
  work: "Travail",
  study: "Études",
  tourism: "Tourisme",
  business: "Affaires",
  other: "Autre",
};

const INTENTION_LABELS_VI: Record<Intention, string> = {
  work: "Làm việc",
  study: "Du học",
  tourism: "Du lịch",
  business: "Kinh doanh",
  other: "Khác",
};

const INTENTION_LABELS_ZH_HANS: Record<Intention, string> = {
  work: "工作",
  study: "留学",
  tourism: "旅游",
  business: "商务",
  other: "其他",
};

// Generated from the zh-Hans table by `toTraditional`; fix meaning there, not here.
const INTENTION_LABELS_ZH_HANT: Record<Intention, string> = {
  work: "工作",
  study: "留學",
  tourism: "旅遊",
  business: "商務",
  other: "其他",
};

/** Locale-aware intention label; EN default, then VI, FR and zh maps. */
export function getIntentionLabel(value: Intention, locale: Locale = "en"): string {
  return localeLabel(locale, value, INTENTION_LABELS[value], {
    vi: INTENTION_LABELS_VI,
    fr: INTENTION_LABELS_FR,
    "zh-Hans": INTENTION_LABELS_ZH_HANS,
    "zh-Hant": INTENTION_LABELS_ZH_HANT,
  });
}

// ── Gating: which prompt (if any) the client dashboard shows ───────────────────

/**
 * Which onboarding prompt the dashboard should surface:
 * - "role"         — the pro/client fork (choice not made yet)
 * - "enrichment"   — the "tell us about your search" form (client, not yet saved/skipped)
 * - "finish-apply" — passive "finish your application" card (pro-intent, no submission yet)
 * - "none"         — onboarding settled
 */
export type OnboardingPromptKind = "role" | "enrichment" | "finish-apply" | "none";

export interface OnboardingPrompt {
  kind: OnboardingPromptKind;
  /** true → the modal auto-opens once this visit; false → passive dismissible
   *  card only (cap reached, or a prompt that never auto-opens). */
  autoOpen: boolean;
}

export interface OnboardingStateInput {
  choice: OnboardingChoice | null;
  enrichmentStatus: EnrichmentStatus | null;
  promptCount: number;
  /** Pro-intent only: do they already have a SUBMITTED application? */
  hasSubmittedApplication: boolean;
}

/**
 * Pure gating decision (unit-tested). A null client_preferences row maps to
 * `{ choice: null, enrichmentStatus: null, promptCount: 0 }`. Nothing here
 * blocks the dashboard — `autoOpen` only decides modal-vs-passive-card.
 */
export function deriveOnboardingPrompt(s: OnboardingStateInput): OnboardingPrompt {
  const underCap = s.promptCount < PROMPT_CAP;

  // 1. Role not chosen yet → the pro/client fork.
  if (s.choice == null) return { kind: "role", autoOpen: underCap };

  // 2. Client who hasn't saved or skipped enrichment → the enrichment form.
  if (s.choice === "client" && s.enrichmentStatus == null) {
    return { kind: "enrichment", autoOpen: underCap };
  }

  // 3. Pro-intent without a submitted application → passive resume card (never
  //    auto-opens; the apply-draft autosave lets them pick up where they left off).
  if (s.choice === "pro" && !s.hasSubmittedApplication) {
    return { kind: "finish-apply", autoOpen: false };
  }

  // 4. Onboarding settled.
  return { kind: "none", autoOpen: false };
}
